import type {
  BaseDimensionCriteria,
  DataSourceCandidatesRequest,
  DimensionDefinition,
  WidgetDimensionsRequest,
  WidgetEntitiesRequest,
} from '@highimpact-frontend/api-types';

import type {
  BuildWidgetDataSourceRequestOptions,
  WidgetDataSourceWidget,
  WidgetSourceScope,
} from './types';
import {
  getDefaultIndependentDimensionKeys,
} from './widgetDimensionSlots';

export {
  getCoupledWidgetDimensionConfig,
  getDefaultIndependentDimensionKeys
} from './widgetDimensionSlots';

// -- type definitions --

type IndependentDimensionMap = NonNullable<
  WidgetDimensionsRequest['independent_dimensions']
>;

type CoupledGroupMap = NonNullable<WidgetDimensionsRequest['coupled_groups']>;

// -- helper functions --

/**
 * Builds shared project and site filters from dashboard scope.
 */
export function buildScopeCriteria(
  scope: WidgetSourceScope
): BaseDimensionCriteria {
  return {
    ...(scope.project_id != null ? { project_id: [scope.project_id] } : {}),
    ...(scope.site_id != null ? { site_id: [scope.site_id] } : {}),
  };
}

/**
 * Creates an independent dimension entry with optional criteria.
 */
function independentDimension(
  criteria?: BaseDimensionCriteria | null
): DimensionDefinition {
  return criteria ? { criteria } : {};
}

/**
 * Builds independent dimension slots from a list of keys and shared scope filters.
 */
function buildIndependentDimensions(
  dimensionKeys: readonly string[],
  scope: WidgetSourceScope
): IndependentDimensionMap {
  const sharedCriteria = buildScopeCriteria(scope);

  return Object.fromEntries(
    dimensionKeys.map((dimensionKey) => [
      dimensionKey,
      independentDimension(
        Object.keys(sharedCriteria).length > 0 ? sharedCriteria : null
      ),
    ])
  );
}

/**
 * Builds a v2 dimensions request for widgets that pick independent measure slots.
 */
function buildIndependentDimensionsRequest(
  widget: WidgetDataSourceWidget,
  scope: WidgetSourceScope,
  independentDimensionKeys?: readonly string[]
): WidgetDimensionsRequest {
  const dimensionKeys =
    independentDimensionKeys ?? getDefaultIndependentDimensionKeys(widget);

  return {
    schema_version: '2.0.0',
    request_type: 'dimensions',
    independent_dimensions: buildIndependentDimensions(dimensionKeys, scope),
  };
}

/**
 * Builds a v2 dimensions request for widgets that pick coupled measure slots.
 */
function buildCoupledDimensionsRequest(
  widget: Extract<WidgetDataSourceWidget, 'windrose' | 'ombrothermic'>,
  scope: WidgetSourceScope
): WidgetDimensionsRequest {
  const sharedCriteria = buildScopeCriteria(scope);
  const coupledGroups: CoupledGroupMap =
    widget === 'windrose'
      ? {
          wind_data: {
            group_by: ['measure_point'],
            shared_criteria:
              Object.keys(sharedCriteria).length > 0 ? sharedCriteria : null,
            dimensions: {
              speed: {
                criteria: {
                  measure_type: ['vitesse_du_vent'],
                },
              },
              direction: {
                criteria: {
                  measure_type: ['direction_du_vent'],
                },
              },
            },
          },
        }
      : {
          climate_data: {
            group_by: ['measure_point'],
            shared_criteria:
              Object.keys(sharedCriteria).length > 0 ? sharedCriteria : null,
            dimensions: {
              pluviometry: {
                criteria: {
                  unit: ['mm/jour'],
                },
              },
              temperature: {
                criteria: {
                  unit: ['°C'],
                },
              },
            },
          },
        };

  return {
    schema_version: '2.0.0',
    request_type: 'dimensions',
    coupled_groups: coupledGroups,
  };
}

/**
 * Builds a v2 entities request for widgets scoped to a project-site pair.
 */
function buildEntitiesRequest(scope: WidgetSourceScope): WidgetEntitiesRequest {
  return {
    schema_version: '2.0.0',
    request_type: 'entities',
    ...(scope.project_id != null ? { project_id: [scope.project_id] } : {}),
  };
}

// -- main --

/**
 * Builds the data-sources-candidates request body for a built-in widget form.
 *
 * @param options - Widget kind, dashboard scope, and optional dimension overrides.
 * @returns A v2 dimensions or entities request ready for `useFetchDataSources`.
 */
export function buildWidgetDataSourceRequest({
  widget,
  scope,
  independentDimensionKeys,
}: BuildWidgetDataSourceRequestOptions): DataSourceCandidatesRequest {
  switch (widget) {
    case 'scatter':
    case 'timeseries':
    case 'histogram':
    case 'aggregation_table':
    case 'table_by_campaign':
      return buildIndependentDimensionsRequest(
        widget,
        scope,
        independentDimensionKeys
      );
    case 'windrose':
    case 'ombrothermic':
      return buildCoupledDimensionsRequest(widget, scope);
    case 'map':
    case 'text_from_entity_property':
    case 'image':
    case 'image_gallery':
      return buildEntitiesRequest(scope);
    default: {
      const unreachableWidget: never = widget;
      throw new Error(`Unsupported widget kind: ${String(unreachableWidget)}`);
    }
  }
}
